import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { agentFullName } from './AgentAvatar';
import type { AgentId } from '@/hooks/useJobProgress';

interface ReviseDialogProps {
  open: boolean;
  agent: AgentId;
  itemKey?: string | null;
  onClose: () => void;
  onSubmit: (feedback: string) => Promise<void> | void;
}

const MAX = 2000;

/**
 * Modal that collects the user's free-form feedback for a 'revise'
 * decision. The text is sent verbatim to the backend, which forwards
 * it to the agent on the next iteration.
 */
export function ReviseDialog({ open, agent, itemKey, onClose, onSubmit }: ReviseDialogProps) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const remaining = MAX - text.length;

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setText('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-display-sm italic text-ink">
            Ask {agentFullName(agent)} to revise.
          </DialogTitle>
          <DialogDescription className="text-graphite">
            Describe what should change. The agent will rerun{itemKey ? <> for <span className="font-mono text-ink">{itemKey}</span></> : null}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <Label htmlFor="revise-feedback" className="smallcaps">Feedback</Label>
          <Textarea
            id="revise-feedback"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX))}
            placeholder="e.g. Move the primary CTA to the bottom-right and use the accent colour."
            rows={6}
            disabled={submitting}
            autoFocus
          />
          <p className="text-right font-mono text-[11px] text-graphite tabular-nums">
            {remaining} characters left
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!text.trim() || submitting}
            variant="accent"
          >
            {submitting ? 'Sending…' : 'Send to agent'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
